import React, { forwardRef, ReactNode, useCallback, useState } from 'react';

import styled from '@mint-lib/styled';
import OutlinedInput, { OutlinedInputProps } from '@mui/material/OutlinedInput';
import { TooltipProps as TooltipPropsM } from '@mui/material/Tooltip';

import HelperText from '../../common/Input/HelperText.jsx';
import InputEndAdornment from '../../common/Input/InputEndAdornment.jsx';
import Label from '../../common/Input/Label.jsx';
import FormControl from '../FormControl/FormControl.jsx';

export interface InputProps extends OutlinedInputProps {
  /**
   *  Input type can be Password or Text
   *  @default text
   *  @deprecated 'number' varian. Use <NumberField /> instead
   */
  type?: 'text' | 'password' | 'number' | 'email';
  /**
   * The value of the input element, required for a controlled component.
   */
  value?: string | number;
  /**
   * The label of the input. It is only used for layout. The actual labelling is handled by InputLabel.
   */
  label?: string;
  /**
   * If true, the input will indicate an error. The prop defaults to the value (false) inherited from the parent FormControl component.
   * @default false
   */
  error?: boolean;
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder?: string;
  /**
   * The helperText of the input. It is only used for layout. The actual labelling is handled by HelperText.
   */
  helperText?: ReactNode;
  /**
   * If true, the component is disabled. The prop defaults to the value (false) inherited from the parent FormControl component.
   * @default false
   */
  disabled?: boolean;
  /**
   * Name attribute of the input element.
   */
  name?: string;
  autoFocus?: boolean;
  /**
   * Signature:
   * function(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
   * event: The event source of the callback. You can pull out the new value by accessing event.target.value (string).
   */
  onChange?: React.ChangeEventHandler;
  /**
   * The color of the component
   * @default primary
   */
  color?: 'primary' | 'secondary';
  /**
   * Ability to stretch the entire width of the container
   * @default false
   */
  fullWidth?: boolean;
  /**
   *  Text to render tooltip for information
   */
  infoText?: string;
  /**
   * Tooltip props
   */
  TooltipProps?: Partial<TooltipPropsM>;
  /**
   * Icon or IconButton placed at the end
   */
  endAdornment?: ReactNode;
  /**
   * Icon or IconButton placed at the end
   */
  startAdornment?: ReactNode;
  /**
   * Maximum number of characters
   */
  maxLength?: number;
  /**
   * ReadOnly property for input
   */
  readOnly?: boolean;
  /**
   * Decimals will not be allowed
   */
  decimalsNotAllowed?: boolean;

  /**
   * Value of decimals limit
   */
  warning?: boolean;
  /**
   * Helper Text Tooltip props
   */
  HelperTextTooltipProps?: Partial<TooltipPropsM>;
}

/**
 * @deprecated Use the new Input component from @mint-lib/tw-components instead.
 */
export default forwardRef(function Input(
  {
    type = 'text',
    color = 'primary',
    infoText,
    TooltipProps,
    endAdornment,
    startAdornment,
    maxLength,
    readOnly,
    decimalsNotAllowed = false,
    warning,
    HelperTextTooltipProps,
    helperText,
    ...props
  }: InputProps,
  ref,
) {
  const [showPassword, setShowPassword] = useState(false);

  const numberInputKeys = ['e', '+', '-'];
  const decimalsNotAllowedKeys = ['.', ','];

  const onKeyDownHandler = useCallback((event: any) => {
    if (type === 'number') {
      numberInputKeys.some((key) => event.key === key) &&
        event.preventDefault();
      if (decimalsNotAllowed) {
        decimalsNotAllowedKeys.some((key) => event.key === key) &&
          event.preventDefault();
      }
    }
    return props.onKeyDown;
  }, []);

  return (
    <StyledFormControl
      error={props.disabled ? false : props.error}
      disabled={props.disabled}
      size={props.size}
      color={color}
      fullWidth={props.fullWidth}
    >
      {props.label ? (
        <Label
          disabled={props.disabled}
          label={props.label}
          infoText={infoText}
          TooltipProps={TooltipProps}
        />
      ) : null}
      <StyledOutlinedInput
        {...props}
        ref={ref}
        className={`${readOnly ? 'read-only-input' : ''} ${
          warning ? 'warning-input' : ''
        } ${props.className}`}
        readOnly={readOnly}
        type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
        onKeyDown={props.onKeyDown && onKeyDownHandler}
        onChange={props.onChange}
        name={props.name}
        value={props.value}
        error={props.disabled ? false : props.error}
        placeholder={props.placeholder}
        disabled={props.disabled}
        fullWidth={props.fullWidth}
        startAdornment={startAdornment}
        sx={{
          width: props.fullWidth ? '100%' : '12.5rem',
          ...props.sx,
        }}
        endAdornment={
          <InputEndAdornment
            endAdornment={endAdornment}
            maxLength={maxLength}
            type={type}
            disabled={props.disabled}
            value={props.value}
            size={props.size}
            setShowPassword={setShowPassword}
            showPassword={showPassword}
          />
        }
        inputProps={{
          maxLength: maxLength,
          ...props.inputProps,
        }}
      />
      {helperText ? (
        <HelperText
          warning={warning}
          HelperTextTooltipProps={HelperTextTooltipProps}
          error={props.error}
          fullWidth={props.fullWidth}
          helperText={helperText}
          helperTextWidth="12.5rem"
        />
      ) : null}
    </StyledFormControl>
  );
});

const StyledOutlinedInput = styled(OutlinedInput)({
  padding: '0px 12px',
  gap: '8px',
  '& input': {
    padding: '0px',
  },
});
const StyledFormControl = styled(FormControl)({
  width: '100%',
  '.MuiInputLabel-root': {
    position: 'relative',
  },
});
